import { Card, Skeleton, SkeletonAvatar, SkeletonButton, SkeletonForm, SkeletonRegion, SkeletonTabs, SkeletonText } from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { R_CARD, R_CONTROL, SHADOW_CARD, FORM_MAX } from './web/SettingsKit';

// ─────────────────────────────────────────────────────────────────────────────
// The settings page's loading shapes.
//
// Desktop: the tab strip card, then the P2 layout the content lands in — the
// main section card holding the profile form (two columns, four fields, the
// save bar) and the 19rem identity rail (96px avatar, name, a button). Mobile:
// the profile hero card and the setting rows under the real MobileHeader.
// ─────────────────────────────────────────────────────────────────────────────

export const SettingsSkeleton = ({ label }) => {
  const { t } = useTranslation();
  const lbl = label ?? t('common.state.loading');
  return (
    <SkeletonRegion label={lbl} data-testid="settings-loading" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Tab strip card — 52px, like the real one */}
      <div aria-hidden style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: R_CARD, boxShadow: SHADOW_CARD, height: 52, display: 'flex', alignItems: 'center', padding: '0 8px', overflow: 'hidden' }}>
        <SkeletonTabs widths={[64, 84, 72, 96, 60, 80]} padding="0 14px" style={{ borderBottom: 'none' }} />
      </div>
      <div className="grid grid-cols-1 min-[1181px]:grid-cols-[minmax(0,1fr)_19rem] gap-5 items-start">
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: R_CARD, padding: 22, boxShadow: SHADOW_CARD, minWidth: 0 }}>
          <div aria-hidden style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
            <Skeleton width={40} height={40} radius={R_CONTROL} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Skeleton width={140} height={16} />
              <Skeleton width={200} height={11} />
            </div>
          </div>
          <SkeletonForm fields={4} columns={2} actions={1} gap={14} style={{ maxWidth: FORM_MAX }} label={lbl} />
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: R_CARD, padding: 22, boxShadow: SHADOW_CARD, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <SkeletonAvatar size={96} />
          <Skeleton width="70%" height={14} style={{ marginTop: 4 }} />
          <Skeleton width="45%" height={11} />
          <SkeletonButton size="sm" width={140} style={{ marginTop: 6 }} />
        </div>
      </div>
    </SkeletonRegion>
  );
};

const MobileSettingRow = ({ i }) => (
  <div aria-hidden style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minHeight: 52, padding: '0 0.875rem', borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
    <Skeleton width={34} height={34} radius={RADIUS.control} />
    <Skeleton width={`${40 + ((i * 13) % 26)}%`} height={13} />
    <Skeleton width={16} height={16} radius={4} style={{ marginInlineStart: 'auto' }} />
  </div>
);

export const SettingsMobileSkeleton = ({ label }) => {
  const { t } = useTranslation();
  return (
    <SkeletonRegion label={label ?? t('common.state.loading')} data-testid="settings-loading" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {/* Profile hero card */}
      <Card aria-hidden style={{ padding: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <SkeletonAvatar size={56} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Skeleton width="60%" height={15} />
          <Skeleton width="40%" height={11} />
        </div>
        <SkeletonButton size="sm" width={72} />
      </Card>
      {/* Setting rows */}
      <Card aria-hidden style={{ padding: 0, overflow: 'hidden' }}>
        {[0, 1, 2, 3].map(i => <MobileSettingRow key={i} i={i} />)}
      </Card>
      <Card aria-hidden style={{ padding: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
        <Skeleton width={110} height={13} />
        <SkeletonText lines={2} height={12} lastWidth="55%" />
      </Card>
    </SkeletonRegion>
  );
};

export default SettingsSkeleton;
